'use client'

import { createContext, useContext, useState, useCallback, useEffect } from 'react'

const CartContext = createContext(null)
const STORAGE_KEY = 'meowmi-cart'

function loadCart() {
  if (typeof window === 'undefined') return []
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved ? JSON.parse(saved) : []
  } catch {
    return []
  }
}

function saveCart(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch { /* storage full or unavailable */ }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState([])
  const [hydrated, setHydrated] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // Load from localStorage after mount — avoids SSR/client hydration mismatch.
  // The set-state-in-effect disable is intentional: this is a one-shot mount-time read
  // from an external store (localStorage) that cannot safely run during render in SSR.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setItems(loadCart())
    setHydrated(true)
  }, [])

  // Sync to localStorage on change, but only after first load so we don't overwrite with []
  useEffect(() => {
    if (!hydrated) return
    saveCart(items)
  }, [items, hydrated])

  const addItem = useCallback((artwork) => {
    // 統一 image：優先用 imageV2（新版高解析）、再退回 image，避免 cart 因欄位不一致而沒圖
    const normalized = {
      ...artwork,
      image: artwork.imageV2 || artwork.image || '',
    }
    setItems((prev) => {
      // 認領圖每張只有一個，不重複加入
      if (prev.some((item) => item.id === normalized.id)) return prev
      return [...prev, normalized]
    })
    setIsDrawerOpen(true)
  }, [])

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((item) => item.id !== id))
  }, [])

  const clearCart = useCallback(() => {
    setItems([])
  }, [])

  const total = items.reduce((sum, item) => sum + (item.price || 0), 0)

  return (
    <CartContext.Provider
      value={{
        items,
        total,
        count: items.length,
        addItem,
        removeItem,
        clearCart,
        isDrawerOpen,
        openDrawer: () => setIsDrawerOpen(true),
        closeDrawer: () => setIsDrawerOpen(false),
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within CartProvider')
  return ctx
}
